import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { SyntheticEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { resetCashInput, setCashInputExpand, setSelectedCashId, setTriggerFocusCompanyNameInput, setCashInput, setCountry, setAccounts, setAccount, setReceipt, setPayment, setDescription, setDate } from './reducers/CashInputReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { CashInputSelector } from './reducers/CashInputReducer';
import CashApi from './api/CashApi';
import CashModel from './models/CashModel';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import { CashAccountSelector, setShowCashAccount } from './reducers/CashAccountReducer';
import { setTriggerRefreshCashTable } from './reducers/CashTableReducer';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';

const CashInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const cashInputViewData = useSelector(CashInputSelector);
    const cashModelData = useSelector(CashAccountSelector);

    useEffect(() => {
        if (cashInputViewData.triggerSave > 0) {
            save();
        }
    }, [cashInputViewData.triggerSave]);

    useEffect(() => {
        if (cashInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [cashInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetCashInput());
        dispatch(setSelectedCashId(""));
        dispatch(setTriggerFocusCompanyNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (cashInputViewData.selectedCashId.length > 0) {
            selectCashById(cashInputViewData.selectedCashId);
        }
    }, [cashInputViewData.selectedCashId])

    const selectCashById = (id: string) => {
        dispatch(showLoading(true));
        CashApi.GetById({
            id: id
        }).then((response: BaseResponse<CashModel>) => {
            if (response.success) {
                dispatch(setCashInput(response.data));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    let getAccountByCountry = () => {
        if (cashModelData.cashAccounts.length == 0 || !cashInputViewData.input.country || cashInputViewData.input.country.length == 0) {
            return [] as string[];
        } else {
            let accounts = [] as string[];
            _.each(cashModelData.cashAccounts, (cashAccount) => {
                if (cashAccount.country == cashInputViewData.input.country) {
                    accounts.push(cashAccount.account);
                }
            });
            return accounts;
        }
    }

    const save = () => {
        try {
            validate();

            CashApi.Save(cashInputViewData.input)
                .then((response: BaseResponse<CashModel>) => {
                    if (response.success) {
                        if (cashInputViewData.input.id.length === 0) {
                            Utility.Alert.Success("Cash added successful");
                        } else {
                            Utility.Alert.Success("Cash updated successful");
                        }

                        refreshInput();

                        dispatch(setTriggerRefreshCashTable((new Date()).getTime()));
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (cashInputViewData.input.updatedDate.length === 0) {
            throw "Date is required";
        }

        if (cashInputViewData.input.country.length === 0) {
            throw "Country is required";
        }

        if (cashInputViewData.input.account.length === 0) {
            throw "Account is required";
        }

        if ((!cashInputViewData.input.receipt || cashInputViewData.input.receipt === 0) && (!cashInputViewData.input.payment || cashInputViewData.input.payment === 0)) {
            throw "Receipt or Payment is required";
        }
    }

    return (
        <>
            {
                <CustomAccordion
                    title="Input"
                    defaultOpen={true}
                    className="top-quick-input"
                    disableHideButton={true}
                >
                    <Grid container>
                        <Grid item lg={3} xs={12}>
                            <Grid container className='cargo-input-component'>
                                <Grid xs={10} lg={9}>
                                    <Autocomplete
                                        className='cargo-input-component'
                                        options={["Vietnam", "Korea", "Singapore", "BVI"]}
                                        renderInput={(params) => <TextField {...params} label="Country" />}
                                        value={cashInputViewData.input.country}
                                        onChange={(event: SyntheticEvent<Element, Event>, value: string | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined) => {
                                            dispatch(setCountry(value ? value : ""));
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={2} lg={3}>
                                    <Tooltip title="Reset (Alt + R)">
                                        <Button
                                            className="clear-input-button"
                                            tabIndex={-1}
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                refreshInput();
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid>
                                <Button
                                    variant="outlined"
                                    color='primary'
                                    onClick={() => {
                                        dispatch(setShowCashAccount(true));
                                    }}
                                >Cash Accounts</Button>
                            </Grid>
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <StartEndDatepicker
                                startDateLabel="Date"
                                useSingleMode={true}
                                useQuickEndDateInput={true}
                                className='cargo-input-component'
                                onStartDateChange={(date: string) => {
                                    if (date) {
                                        dispatch(setDate(date));
                                    }
                                }}
                                startDateValue={cashInputViewData.input.updatedDate.toUpperCase()}
                            >
                            </StartEndDatepicker>
                            <Autocomplete
                                className='cargo-input-component'
                                options={getAccountByCountry()}
                                renderInput={(params) => <TextField {...params} label="Account" />}
                                value={cashInputViewData.input.account}
                                onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                    dispatch(setAccount(value ? value : ""));
                                }}
                            />
                        </Grid>
                        <Grid item lg={3} xs={12}>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Receipt"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={cashInputViewData.input.receipt ? cashInputViewData.input.receipt : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setReceipt(value));
                                }}
                            >
                            </TextField>
                            <TextField
                                className='cargo-input-component'
                                fullWidth
                                label="Payment"
                                InputProps={{
                                    inputComponent: NumberFormatCustom as any,
                                }}
                                value={cashInputViewData.input.payment ? cashInputViewData.input.payment : ""}
                                onChange={(event) => {
                                    let value = event.target.value.length == 0 ? 0 : parseFloat(event.target.value);
                                    dispatch(setPayment(value));
                                }}
                            >
                            </TextField>
                        </Grid>
                        <Grid item lg={2} xs={12}>
                            <TextField
                                label="Note"
                                multiline
                                fullWidth
                                rows={3}
                                value={cashInputViewData.input.description}
                                onChange={(event) => {
                                    dispatch(setDescription(event.target.value));
                                }}
                                className="cargo-input-component tonnage-input-full-tc"
                            />
                        </Grid>

                        <Grid item lg={1} xs={12}>
                            <Tooltip title="Alt + S">
                                <Button
                                    onClick={save}
                                    variant="outlined"
                                >
                                    Save (s)
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </CustomAccordion>
            }
        </>
    )
};

export default CashInput;